import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';

import { BookOpen, FileText, LogOut, MessageSquare, MoreHorizontal, Shield } from 'lucide-react';

import { useAuth } from '@/app/providers/AuthProvider';
import { useUser } from '@/app/providers/UserProvider';
import imgIconMoon from '@/assets/icons/icon-moon.png';
import imgIconMoonInactive from '@/assets/icons/icon-moon-inactive.png';
import imgIconSun from '@/assets/icons/icon-sun.png';
import imgIconSunActive from '@/assets/icons/icon-sun-active.png';
// 导航图标资产（来自 Figma 设计稿 node-id: 63:621，通过 webpack 模块导入）
import imgLogoDark from '@/assets/icons/logo-dark.png';
import imgLogoLight from '@/assets/icons/logo-light.png';
import imgNavProject from '@/assets/icons/nav-project.svg';
import imgNavSkill from '@/assets/icons/nav-skill.svg';
import imgNavTeam from '@/assets/icons/nav-team.svg';
import imgStarDark from '@/assets/icons/star-dark.png';
import imgStarLight from '@/assets/icons/star-light.png';
import imgUserAvatarDark from '@/assets/icons/user-avatar-default-dark.png';
import imgUserAvatarLight from '@/assets/icons/user-avatar-default-light.png';
import {
  type BillingDateRange,
  buildBillingUsageQuery,
  buildPointsDisplay,
  buildPointsTitle,
  createBillingDateRange,
  getBillingDateRangeBounds,
  GlobalBillingUsageModal,
  type PaymentChannelAction,
  PaymentOrdersModal,
  PointsBuyModal,
  PointsCenterModal,
  PointsGiftRechargeModal,
  PointsHistoryModal,
  PointsPurchaseModal,
  PointsSummaryProvider,
  type PointsSummaryRefreshOptions,
  resolvePaymentChannelAction,
} from '@/features/billing';
import { APP_PATHS } from '@/lib/navigation';
import { PaymentApi } from '@/services/api/paymentApi';
import { PointsApi } from '@/services/api/pointsApi';
import { UserApi } from '@/services/api/userApi';
import { createPointsWebSocket, type PointsWebSocket } from '@/services/websocket/pointsWebSocket';
import type {
  GlobalBillingUsage,
  PaymentCreateOrderResponse,
  PaymentMethod,
  PaymentOrdersResponse,
  PaymentOrderStatus,
  PaymentProduct,
  PointsHistoryCategory,
  PointsHistoryData,
  PointsRechargeGiftRequest,
  PointsSessionUsageItem,
  PointsSummary,
} from '@/types';

interface LayoutProps {
  children: React.ReactNode;
  theme: 'light' | 'dark';
  onThemeToggle: () => void;
  fullscreen?: boolean;
}

interface NavItem {
  icon?: string;
  label: string;
  to: string;
  fullLabel: string;
}

const navItems: NavItem[] = [
  { icon: imgNavProject, label: '项目', to: APP_PATHS.projects, fullLabel: '项目管理' },
  { icon: imgNavSkill, label: '技能', to: '/skills', fullLabel: '技能管理' },
];

const POINTS_HISTORY_PAGE_SIZE = 20;
const PAYMENT_ORDERS_PAGE_SIZE = 10;
const INITIAL_POINTS_HISTORY_PAGES: Record<PointsHistoryCategory, number> = {
  all: 1,
  earn: 1,
  refund: 1,
  consume: 1,
};

const executePaymentChannelAction = (action: PaymentChannelAction): void => {
  if (action.type === 'redirect') {
    window.open(action.url, '_blank', 'noopener,noreferrer');
    return;
  }
  if (action.type === 'submit-form') {
    const container = document.createElement('div');
    container.style.display = 'none';
    container.innerHTML = action.html;
    document.body.appendChild(container);
    const form = container.querySelector('form');
    if (form) {
      form.setAttribute('target', '_blank');
      form.submit();
    }
    window.setTimeout(() => container.remove(), 1000);
  }
};

const Layout: React.FC<LayoutProps> = ({ children, theme, onThemeToggle, fullscreen = false }) => {
  const { activeUser } = useUser();
  const { isLeader, logout, uid } = useAuth();
  const [open, setOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [pointsSummary, setPointsSummary] = useState<PointsSummary | null>(null);
  const [pointsLoading, setPointsLoading] = useState(false);
  const [pointsError, setPointsError] = useState<string | null>(null);
  const [pointsCenterOpen, setPointsCenterOpen] = useState(false);
  const [pointsHistoryOpen, setPointsHistoryOpen] = useState(false);
  const [pointsHistory, setPointsHistory] = useState<PointsHistoryData | null>(null);
  const [pointsHistoryLoading, setPointsHistoryLoading] = useState(false);
  const [pointsHistoryError, setPointsHistoryError] = useState<string | null>(null);
  const [pointsHistoryCategory, setPointsHistoryCategory] = useState<PointsHistoryCategory>('all');
  const [pointsHistoryPages, setPointsHistoryPages] = useState<
    Record<PointsHistoryCategory, number>
  >(INITIAL_POINTS_HISTORY_PAGES);
  const [pointsHistoryDateRange, setPointsHistoryDateRange] = useState<BillingDateRange>(() =>
    createBillingDateRange('last7days'),
  );
  const [pointsHistoryProjectKey, setPointsHistoryProjectKey] = useState('');
  const [pointsHistoryConversationKey, setPointsHistoryConversationKey] = useState('');
  const [pointsHistorySessionOptions, setPointsHistorySessionOptions] = useState<
    PointsSessionUsageItem[]
  >([]);
  const [pointsHistorySessionOptionsLoading, setPointsHistorySessionOptionsLoading] =
    useState(false);
  const [canViewGlobalBilling, setCanViewGlobalBilling] = useState(false);
  const [globalBillingOpen, setGlobalBillingOpen] = useState(false);
  const [globalBillingUsage, setGlobalBillingUsage] = useState<GlobalBillingUsage | null>(null);
  const [globalBillingLoading, setGlobalBillingLoading] = useState(false);
  const [globalBillingError, setGlobalBillingError] = useState<string | null>(null);
  const [adminRechargeOpen, setAdminRechargeOpen] = useState(false);
  const [adminRechargeLoading, setAdminRechargeLoading] = useState(false);
  const [adminRechargeError, setAdminRechargeError] = useState<string | null>(null);
  const [adminRechargeSuccess, setAdminRechargeSuccess] = useState<string | null>(null);
  const [paymentProducts, setPaymentProducts] = useState<PaymentProduct[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | null>(null);
  const [paymentProductsLoading, setPaymentProductsLoading] = useState(false);
  const [purchasingProductId, setPurchasingProductId] = useState<string | null>(null);
  const [paymentPurchaseOpen, setPaymentPurchaseOpen] = useState(false);
  const [paymentPurchaseOrder, setPaymentPurchaseOrder] =
    useState<PaymentCreateOrderResponse | null>(null);
  const [paymentPurchaseStatus, setPaymentPurchaseStatus] = useState<PaymentOrderStatus | null>(
    null,
  );
  const [paymentPurchaseChannelAction, setPaymentPurchaseChannelAction] =
    useState<PaymentChannelAction>({ type: 'none' });
  const [paymentPurchaseRefreshing, setPaymentPurchaseRefreshing] = useState(false);
  const [paymentPurchaseError, setPaymentPurchaseError] = useState<string | null>(null);
  const [pointsBuyOpen, setPointsBuyOpen] = useState(false);
  const [paymentOrdersOpen, setPaymentOrdersOpen] = useState(false);
  const [paymentOrders, setPaymentOrders] = useState<PaymentOrdersResponse | null>(null);
  const [paymentOrdersLoading, setPaymentOrdersLoading] = useState(false);
  const [paymentOrdersError, setPaymentOrdersError] = useState<string | null>(null);
  const [paymentOrdersPage, setPaymentOrdersPage] = useState(1);
  const [globalBillingDateRange, setGlobalBillingDateRange] = useState<BillingDateRange>(() =>
    createBillingDateRange('last7days'),
  );
  const userMenuRef = useRef<HTMLDivElement>(null);
  const moreMenuRef = useRef<HTMLDivElement>(null);
  const pointsSummaryLoadingRef = useRef(false);
  const pointsWebSocketRef = useRef<PointsWebSocket | null>(null);
  const userNickname = String(activeUser.nickname || '').trim();
  const userPhoneLabel = String(activeUser.phone || '').trim();
  const userIdLabel = String(activeUser.id || uid || '').trim();
  const userDisplayName = userNickname || userPhoneLabel || userIdLabel;
  const userLabel =
    userNickname && userPhoneLabel ? `${userNickname} (${userPhoneLabel})` : userDisplayName;
  const pointsDisplay = buildPointsDisplay(pointsSummary);
  const pointsTitle = buildPointsTitle({
    summary: pointsSummary,
    error: pointsError,
    loading: pointsLoading,
  });
  const visibleNavItems = useMemo(
    () =>
      isLeader
        ? [...navItems, { icon: imgNavTeam, label: '团队', to: '/team', fullLabel: '团队管理' }]
        : navItems,
    [isLeader],
  );

  const loadPointsSummary = useCallback(
    async (options: PointsSummaryRefreshOptions = {}) => {
      if (!uid) {
        setPointsSummary(null);
        return;
      }
      if (pointsSummaryLoadingRef.current) return;

      pointsSummaryLoadingRef.current = true;
      const silent = options.silent === true;
      if (!silent) {
        setPointsLoading(true);
        setPointsError(null);
      }
      try {
        const response = await PointsApi.getSummary();
        if (response.status !== 200) {
          throw new Error(response.message || '积分查询失败');
        }
        setPointsSummary(response.data);
        setPointsError(null);
      } catch (error) {
        if (!silent) {
          setPointsError(error instanceof Error ? error.message : '积分查询失败');
        }
      } finally {
        pointsSummaryLoadingRef.current = false;
        if (!silent) {
          setPointsLoading(false);
        }
      }
    },
    [uid],
  );

  const loadPaymentProducts = useCallback(async () => {
    if (!uid) {
      setPaymentProducts([]);
      setPaymentMethods([]);
      setSelectedPaymentMethodId(null);
      return;
    }
    setPaymentProductsLoading(true);
    try {
      const response = await PaymentApi.getProducts();
      setPaymentProducts(response.products);
      const nextPaymentMethods = response.payment_methods ?? [];
      setPaymentMethods(nextPaymentMethods);
      setSelectedPaymentMethodId((current) => {
        if (current && nextPaymentMethods.some((method) => method.method_id === current)) {
          return current;
        }
        return nextPaymentMethods[0]?.method_id ?? null;
      });
    } catch {
      setPaymentProducts([]);
      setPaymentMethods([]);
      setSelectedPaymentMethodId(null);
    } finally {
      setPaymentProductsLoading(false);
    }
  }, [uid]);

  const loadPaymentOrders = useCallback(
    async (page: number) => {
      if (!uid) {
        setPaymentOrders(null);
        return;
      }
      const nextPage = Math.max(1, page);
      setPaymentOrdersLoading(true);
      setPaymentOrdersError(null);
      try {
        const response = await PaymentApi.listOrders({
          page: nextPage,
          pageSize: PAYMENT_ORDERS_PAGE_SIZE,
        });
        setPaymentOrders(response);
        setPaymentOrdersPage(response.page);
      } catch (error) {
        setPaymentOrdersError(error instanceof Error ? error.message : '充值记录加载失败');
      } finally {
        setPaymentOrdersLoading(false);
      }
    },
    [uid],
  );

  const handleOpenPointsCenter = useCallback(() => {
    setOpen(false);
    setPointsCenterOpen(true);
    void loadPointsSummary();
  }, [loadPointsSummary]);

  const handleOpenPointsBuy = useCallback(() => {
    setPointsBuyOpen(true);
    void loadPaymentProducts();
  }, [loadPaymentProducts]);

  const pointsSummaryContextValue = useMemo(
    () => ({
      summary: pointsSummary,
      loading: pointsLoading,
      error: pointsError,
      display: pointsDisplay,
      title: pointsTitle,
      refresh: loadPointsSummary,
      openCenter: handleOpenPointsCenter,
    }),
    [
      handleOpenPointsCenter,
      loadPointsSummary,
      pointsDisplay,
      pointsError,
      pointsLoading,
      pointsSummary,
      pointsTitle,
    ],
  );

  const loadPointsHistory = useCallback(
    async (
      options: {
        category?: PointsHistoryCategory;
        page?: number;
        dateRange?: BillingDateRange;
        projectKey?: string;
        conversationKey?: string;
      } = {},
    ) => {
      if (!uid) {
        setPointsHistory(null);
        return;
      }
      const category = options.category ?? pointsHistoryCategory;
      const page = options.page ?? pointsHistoryPages[category] ?? 1;
      const dateRange = options.dateRange ?? pointsHistoryDateRange;
      const projectKey = options.projectKey ?? pointsHistoryProjectKey;
      const conversationKey = options.conversationKey ?? pointsHistoryConversationKey;
      setPointsHistoryLoading(true);
      setPointsHistoryError(null);
      try {
        const dateBounds = getBillingDateRangeBounds(dateRange);
        const response = await PointsApi.getHistory({
          page,
          pageSize: POINTS_HISTORY_PAGE_SIZE,
          category,
          startAt: dateBounds.startAt,
          endAt: dateBounds.endAt,
          projectKey: projectKey || undefined,
          conversationKey: conversationKey || undefined,
        });
        if (response.status !== 200) {
          throw new Error(response.message || '积分明细加载失败');
        }
        setPointsHistory(response.data);
        setPointsHistoryPages((prev) => ({ ...prev, [category]: page }));
      } catch (error) {
        setPointsHistoryError(error instanceof Error ? error.message : '积分明细加载失败');
      } finally {
        setPointsHistoryLoading(false);
      }
    },
    [
      pointsHistoryCategory,
      pointsHistoryConversationKey,
      pointsHistoryDateRange,
      pointsHistoryPages,
      pointsHistoryProjectKey,
      uid,
    ],
  );

  // 项目筛选变更后拉取该项目的会话选项，失败静默置空不阻塞流水展示。
  const loadPointsHistorySessionOptions = useCallback(async (projectKey: string) => {
    if (!projectKey) {
      setPointsHistorySessionOptions([]);
      setPointsHistorySessionOptionsLoading(false);
      return;
    }
    setPointsHistorySessionOptionsLoading(true);
    try {
      const response = await PointsApi.getSessionsUsage(projectKey);
      setPointsHistorySessionOptions(response.items ?? []);
    } catch {
      setPointsHistorySessionOptions([]);
    } finally {
      setPointsHistorySessionOptionsLoading(false);
    }
  }, []);

  const loadBillingAccess = useCallback(async () => {
    if (!uid) {
      setCanViewGlobalBilling(false);
      return;
    }
    try {
      const access = await UserApi.getBillingAccess();
      setCanViewGlobalBilling(access.can_view_global);
    } catch {
      setCanViewGlobalBilling(false);
    }
  }, [uid]);

  const loadGlobalBillingUsage = useCallback(async (dateRange: BillingDateRange) => {
    setGlobalBillingLoading(true);
    setGlobalBillingError(null);
    try {
      const usage = await UserApi.getGlobalBillingUsage(buildBillingUsageQuery(dateRange));
      setGlobalBillingUsage(usage);
    } catch (error) {
      setGlobalBillingError(error instanceof Error ? error.message : '全局积分加载失败');
    } finally {
      setGlobalBillingLoading(false);
    }
  }, []);

  const handleOpenPointsHistory = () => {
    const defaultRange = createBillingDateRange('last7days');
    setPointsHistoryCategory('all');
    setPointsHistoryPages(INITIAL_POINTS_HISTORY_PAGES);
    setPointsHistoryDateRange(defaultRange);
    setPointsHistoryProjectKey('');
    setPointsHistoryConversationKey('');
    setPointsHistorySessionOptions([]);
    setPointsHistorySessionOptionsLoading(false);
    setOpen(false);
    setPointsCenterOpen(false);
    setPointsHistoryOpen(true);
    void loadPointsHistory({
      category: 'all',
      page: 1,
      dateRange: defaultRange,
      projectKey: '',
      conversationKey: '',
    });
  };

  const handleOpenPaymentOrders = () => {
    setOpen(false);
    setPointsCenterOpen(false);
    setPaymentOrdersPage(1);
    setPaymentOrdersOpen(true);
    void loadPaymentOrders(1);
  };

  const handleOpenGlobalBillingUsage = () => {
    const defaultRange = createBillingDateRange('last7days');
    setGlobalBillingDateRange(defaultRange);
    setOpen(false);
    setPointsCenterOpen(false);
    setGlobalBillingOpen(true);
    void loadGlobalBillingUsage(defaultRange);
  };

  const handleOpenAdminRecharge = () => {
    if (!canViewGlobalBilling) return;
    setOpen(false);
    setPointsCenterOpen(false);
    setAdminRechargeError(null);
    setAdminRechargeSuccess(null);
    setAdminRechargeOpen(true);
  };

  const handleSubmitAdminRecharge = async (input: PointsRechargeGiftRequest) => {
    setAdminRechargeLoading(true);
    setAdminRechargeError(null);
    setAdminRechargeSuccess(null);
    try {
      const response = await PointsApi.rechargeGiftPoints(input);
      if (response.status !== 200) {
        throw new Error(response.message || '积分赠送失败');
      }
      const targetText = input.allUsers
        ? '全部用户'
        : input.userIds
          ? `${input.userIds.length} 个用户`
          : `用户 ${input.userId}`;
      setAdminRechargeSuccess(`已赠送 ${input.points} 积分给${targetText}`);
      void loadPointsSummary();
    } catch (error) {
      setAdminRechargeError(error instanceof Error ? error.message : '积分赠送失败');
    } finally {
      setAdminRechargeLoading(false);
    }
  };

  const refreshPaymentPurchase = useCallback(
    async (options: { silent?: boolean } = {}) => {
      if (!paymentPurchaseOrder) return;
      const silent = options.silent === true;
      if (!silent) {
        setPaymentPurchaseRefreshing(true);
      }
      setPaymentPurchaseError(null);
      try {
        const latest = await PaymentApi.refreshOrder(paymentPurchaseOrder.order_id);
        setPaymentPurchaseStatus(latest);
        if (latest.pay_status === 'PAID' && latest.credit_status === 'SUCCESS') {
          void loadPointsSummary({ silent: true });
        }
      } catch (error) {
        if (!silent) {
          setPaymentPurchaseError(error instanceof Error ? error.message : '支付状态刷新失败');
        }
      } finally {
        if (!silent) {
          setPaymentPurchaseRefreshing(false);
        }
      }
    },
    [loadPointsSummary, paymentPurchaseOrder],
  );

  const handlePurchaseProduct = useCallback(
    async (product: PaymentProduct, paymentMethodId: string) => {
      setPurchasingProductId(product.product_id);
      setPaymentPurchaseError(null);
      try {
        const order = await PaymentApi.createOrder({
          productId: product.product_id,
          paymentMethodId,
        });
        const channelAction = resolvePaymentChannelAction(
          order.channel_return_body,
          order.payment_method,
        );
        setPaymentPurchaseOrder(order);
        setPaymentPurchaseStatus(null);
        setPaymentPurchaseChannelAction(channelAction);
        setPaymentPurchaseOpen(true);
        executePaymentChannelAction(channelAction);
        window.setTimeout(() => {
          void PaymentApi.refreshOrder(order.order_id).then((latest) => {
            setPaymentPurchaseStatus(latest);
            if (latest.pay_status === 'PAID' && latest.credit_status === 'SUCCESS') {
              void loadPointsSummary({ silent: true });
            }
          });
        }, 1500);
      } catch (error) {
        setPaymentPurchaseError(error instanceof Error ? error.message : '支付订单创建失败');
      } finally {
        setPurchasingProductId(null);
      }
    },
    [loadPointsSummary],
  );

  useEffect(() => {
    if (!paymentPurchaseOpen || !paymentPurchaseOrder) return undefined;
    if (
      paymentPurchaseStatus?.pay_status === 'PAID' &&
      paymentPurchaseStatus.credit_status === 'SUCCESS'
    ) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      void refreshPaymentPurchase({ silent: true });
    }, 5000);
    return () => window.clearInterval(timer);
  }, [
    paymentPurchaseOpen,
    paymentPurchaseOrder,
    paymentPurchaseStatus?.credit_status,
    paymentPurchaseStatus?.pay_status,
    refreshPaymentPurchase,
  ]);

  const handleBackToPointsCenter = () => {
    setPointsHistoryOpen(false);
    setPaymentOrdersOpen(false);
    setPointsCenterOpen(true);
  };

  const handlePointsHistoryCategoryChange = (category: PointsHistoryCategory) => {
    setPointsHistoryCategory(category);
    const page = pointsHistoryPages[category] ?? 1;
    if (category !== 'all') {
      // 非「全部」tab 不展示项目/会话筛选，同步清空筛选状态避免隐形过滤。
      setPointsHistoryProjectKey('');
      setPointsHistoryConversationKey('');
      setPointsHistorySessionOptions([]);
      setPointsHistorySessionOptionsLoading(false);
      setPointsHistoryPages(INITIAL_POINTS_HISTORY_PAGES);
      void loadPointsHistory({
        category,
        page: 1,
        dateRange: pointsHistoryDateRange,
        projectKey: '',
        conversationKey: '',
      });
      return;
    }
    void loadPointsHistory({ category, page, dateRange: pointsHistoryDateRange });
  };

  const handlePointsHistoryDateRangeChange = (dateRange: BillingDateRange) => {
    setPointsHistoryDateRange(dateRange);
    setPointsHistoryPages(INITIAL_POINTS_HISTORY_PAGES);
    void loadPointsHistory({ category: pointsHistoryCategory, page: 1, dateRange });
  };

  const handlePointsHistoryPageChange = (page: number) => {
    const nextPage = Math.max(1, page);
    setPointsHistoryPages((prev) => ({ ...prev, [pointsHistoryCategory]: nextPage }));
    void loadPointsHistory({
      category: pointsHistoryCategory,
      page: nextPage,
      dateRange: pointsHistoryDateRange,
    });
  };

  const handlePointsHistoryProjectFilterChange = (projectKey: string) => {
    setPointsHistoryProjectKey(projectKey);
    // 旧会话不属于新项目，不清空会导致结果恒空。
    setPointsHistoryConversationKey('');
    setPointsHistoryPages(INITIAL_POINTS_HISTORY_PAGES);
    void loadPointsHistorySessionOptions(projectKey);
    void loadPointsHistory({ page: 1, projectKey, conversationKey: '' });
  };

  const handlePointsHistorySessionFilterChange = (conversationKey: string) => {
    setPointsHistoryConversationKey(conversationKey);
    setPointsHistoryPages(INITIAL_POINTS_HISTORY_PAGES);
    void loadPointsHistory({ page: 1, conversationKey });
  };

  const handlePaymentOrdersPageChange = (page: number) => {
    const nextPage = Math.max(1, page);
    setPaymentOrdersPage(nextPage);
    void loadPaymentOrders(nextPage);
  };

  const handleGlobalBillingDateRangeChange = (dateRange: BillingDateRange) => {
    setGlobalBillingDateRange(dateRange);
    void loadGlobalBillingUsage(dateRange);
  };

  useEffect(() => {
    void loadBillingAccess();
  }, [loadBillingAccess]);

  useEffect(() => {
    void loadPointsSummary();
  }, [loadPointsSummary]);

  useEffect(() => {
    const handleDailyAccessRecorded = () => {
      void loadPointsSummary({ silent: true });
    };

    window.addEventListener('points:daily-access-recorded', handleDailyAccessRecorded);
    return () => {
      window.removeEventListener('points:daily-access-recorded', handleDailyAccessRecorded);
    };
  }, [loadPointsSummary]);

  useEffect(() => {
    if (!uid) {
      pointsWebSocketRef.current?.close();
      pointsWebSocketRef.current = null;
      return;
    }

    const pointsWebSocket = createPointsWebSocket();
    pointsWebSocketRef.current = pointsWebSocket;
    pointsWebSocket.setCallbacks({
      onSummaryUpdated: (summary) => {
        setPointsSummary(summary);
        setPointsError(null);
      },
      onSummaryInvalidated: () => {
        void loadPointsSummary({ silent: true });
      },
    });
    pointsWebSocket.connect();

    return () => {
      if (pointsWebSocketRef.current === pointsWebSocket) {
        pointsWebSocketRef.current = null;
      }
      pointsWebSocket.close();
    };
  }, [loadPointsSummary, uid]);

  useEffect(() => {
    if (!open && !moreMenuOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (open && userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
      if (
        moreMenuOpen &&
        moreMenuRef.current &&
        !moreMenuRef.current.contains(event.target as Node)
      ) {
        setMoreMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, moreMenuOpen]);

  return (
    <div className="h-screen w-screen bg-dark-bg text-dark-text overflow-hidden">
      <div className="flex h-full">
        <aside
          className={`w-[76px] bg-sidebar-bg flex-col items-center py-8 ${fullscreen ? 'hidden' : 'flex'}`}
        >
          {/* Logo - 根据主题切换日夜间图片 */}
          <div className="mb-4 flex items-center justify-center w-[60px] h-8">
            <img
              src={theme === 'dark' ? imgLogoDark : imgLogoLight}
              alt="ManClaw Logo"
              width={32}
              height={32}
              className="w-8 h-8 object-contain"
            />
          </div>

          <nav className="flex-1 flex flex-col items-center w-[60px] mt-2">
            {visibleNavItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                title={item.fullLabel}
                className={({ isActive }) =>
                  `w-[60px] h-[60px] flex flex-col items-center justify-center rounded-lg transition-colors duration-base ease-base ${
                    isActive ? 'bg-theme-color-10' : ''
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <img
                      src={item.icon}
                      alt={item.fullLabel}
                      className="w-[18px] h-[18px] mb-[6px] transition-all"
                      style={{
                        filter: isActive
                          ? theme === 'dark'
                            ? // 夜间主题色 #2ad99f
                              'brightness(0) saturate(100%) invert(73%) sepia(47%) saturate(450%) hue-rotate(115deg) brightness(100%) contrast(90%)'
                            : // 日间主题色 #23b383
                              'brightness(0) saturate(100%) invert(60%) sepia(50%) saturate(400%) hue-rotate(120deg) brightness(90%) contrast(90%)'
                          : theme === 'dark'
                            ? 'brightness(0) saturate(100%) invert(100%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(100%)'
                            : 'brightness(0) saturate(100%) invert(3%) sepia(10%) saturate(7000%) hue-rotate(210deg) brightness(95%) contrast(100%)',
                        opacity: isActive ? 1 : 0.5,
                      }}
                    />
                    <span
                      className="text-xxs leading-none transition-colors duration-base ease-base"
                      style={{
                        color: isActive ? 'rgb(var(--color-theme))' : 'var(--nav-inactive-color)',
                      }}
                    >
                      {item.label}
                    </span>
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div ref={userMenuRef} className="flex flex-col items-center gap-3 relative">
            <button
              type="button"
              data-testid="sidebar-points-center"
              title="查看积分中心"
              aria-label={pointsTitle}
              onClick={handleOpenPointsCenter}
              className={`mb-4 flex w-10 flex-col items-center justify-center px-0 py-0 ${
                pointsError ? 'text-accent-red' : 'text-dark-text'
              }`}
            >
              <span
                data-testid="sidebar-points-value"
                className="whitespace-nowrap text-center text-[14px] font-normal leading-none tracking-normal tabular-nums"
                style={{ fontFamily: 'AlibabaSans102-Bd' }}
              >
                {pointsDisplay}
              </span>
              <span
                data-testid="sidebar-points-label"
                className="mt-1 inline-flex items-center justify-center gap-0.5 whitespace-nowrap text-[11px] font-semibold leading-none tracking-normal text-theme-color"
              >
                <img
                  src={theme === 'dark' ? imgStarDark : imgStarLight}
                  alt="积分"
                  className="h-3 w-3"
                />
                积分
              </span>
            </button>

            {/* 主题切换胶囊 */}
            <div className="relative border border-border-primary rounded-[40px] overflow-hidden w-8 h-[72px] bg-[rgba(255,255,255,0.05)]">
              {/* 毛玻璃背景 */}
              <div className="absolute inset-[1px] rounded-[24px] bg-[rgba(255,255,255,0.05)]" />

              {/* 绿色指示器：夜间在上(top-[7px])，日间在下(top-[39px]) */}
              <div
                className={`absolute left-[3px] w-6 h-6 rounded-full bg-theme-color transition-all duration-slow ease-base ${
                  theme === 'dark'
                    ? 'shadow-[0px_10px_15px_0px_rgba(0,0,0,0.1),0px_4px_6px_0px_rgba(0,0,0,0.1)]'
                    : ''
                }`}
                style={{ top: theme === 'dark' ? '7px' : '39px' }}
              >
                {theme === 'dark' && (
                  <div className="absolute -inset-[1px] rounded-full bg-theme-color blur-[8px]" />
                )}
              </div>

              {/* 月亮按钮（上方，夜间模式） */}
              <button
                type="button"
                onClick={onThemeToggle}
                className="absolute left-[3px] top-[7px] w-6 h-6 flex items-center justify-center z-10"
                title="切换到浅色模式"
              >
                <img
                  src={theme === 'dark' ? imgIconMoon : imgIconMoonInactive}
                  alt="夜间模式"
                  className="w-[14px] h-[14px] object-contain"
                />
              </button>

              {/* 太阳按钮（下方，日间模式） */}
              <button
                type="button"
                onClick={onThemeToggle}
                className="absolute left-[3px] top-[39px] w-6 h-6 flex items-center justify-center z-10"
                title="切换到深色模式"
              >
                <img
                  src={theme === 'light' ? imgIconSunActive : imgIconSun}
                  alt="日间模式"
                  className="w-[14px] h-[14px] object-contain"
                />
              </button>
            </div>

            {/* 用户头像按钮 */}
            <button
              type="button"
              className="relative w-8 h-8 rounded-full flex items-center justify-center hover:opacity-80 transition-opacity cursor-pointer overflow-hidden"
              title={userLabel}
              onClick={() => {
                setOpen((prev) => !prev);
                setMoreMenuOpen(false);
              }}
            >
              {activeUser.avatar ? (
                <img
                  src={activeUser.avatar}
                  alt={userDisplayName || '用户头像'}
                  className="w-full h-full object-cover"
                />
              ) : (
                <img
                  src={theme === 'dark' ? imgUserAvatarDark : imgUserAvatarLight}
                  alt="用户头像"
                  className="w-full h-full object-cover"
                />
              )}
            </button>

            {open ? (
              <div className="absolute bottom-0 left-12 w-56 bg-dark-card border border-dark-border rounded-xl shadow-xl p-2 z-50">
                <div className="px-2 py-2 border-b border-dark-border mb-1 space-y-1">
                  {userNickname ? (
                    <div className="flex text-sm">
                      <span className="text-dark-text-secondary w-[56px] flex-shrink-0">昵称</span>
                      <span className="text-dark-text">{userNickname}</span>
                    </div>
                  ) : null}
                  {userPhoneLabel ? (
                    <div className="flex text-sm">
                      <span className="text-dark-text-secondary w-[56px] flex-shrink-0">
                        手机号
                      </span>
                      <span className="text-dark-text">{userPhoneLabel}</span>
                    </div>
                  ) : null}
                  {userIdLabel ? (
                    <div className="flex text-sm">
                      <span className="text-dark-text-secondary w-[56px] flex-shrink-0">ID</span>
                      <span className="text-dark-text">{userIdLabel}</span>
                    </div>
                  ) : null}
                </div>
                <div className="pt-1">
                  <button
                    type="button"
                    className="flex w-full items-center px-2 py-2 text-left rounded-lg transition-colors text-accent-red hover:bg-accent-red-10"
                    onClick={() => {
                      setOpen(false);
                      void logout();
                    }}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    退出登录
                  </button>
                </div>
              </div>
            ) : null}

            {/* 更多菜单按钮 */}
            <div ref={moreMenuRef} className="relative">
              <button
                type="button"
                className="relative w-8 h-8 rounded-full border border-border-primary bg-[rgba(255,255,255,0.05)] flex items-center justify-center hover:bg-[rgba(255,255,255,0.1)] transition-colors cursor-pointer"
                title="更多"
                onClick={() => {
                  setMoreMenuOpen((prev) => !prev);
                  setOpen(false);
                }}
              >
                <MoreHorizontal className="w-[18px] h-[18px] text-dark-text opacity-60" />
              </button>
              {moreMenuOpen ? (
                <div className="absolute bottom-0 left-12 w-[152px] bg-dark-card border border-dark-border rounded-xl shadow-xl p-2 z-50">
                  <a
                    href="/#/guide"
                    onClick={() => setMoreMenuOpen(false)}
                    className="flex w-full items-center gap-2 px-2 py-2 text-sm text-dark-text rounded-lg transition-colors hover:text-theme-color active:text-theme-color"
                  >
                    <BookOpen className="h-4 w-4 opacity-30" />
                    用户指南
                  </a>
                  <a
                    href={`https://survey.alibaba.com/apps/zhiliao/jtphFWlA7?uid=${encodeURIComponent(uid ?? '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex w-full items-center gap-2 px-2 py-2 text-sm text-dark-text rounded-lg transition-colors hover:text-theme-color active:text-theme-color"
                  >
                    <MessageSquare className="h-4 w-4 opacity-30" />
                    用户反馈
                  </a>
                  <a
                    href="https://terms.alicdn.com/legal-agreement/terms/c_platform_service_agreement/20260311163335385/20260311163335385.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex w-full items-center gap-2 px-2 py-2 text-sm text-dark-text rounded-lg transition-colors hover:text-theme-color active:text-theme-color"
                  >
                    <FileText className="h-4 w-4 opacity-30" />
                    用户协议
                  </a>
                  <a
                    href="https://terms.alicdn.com/legal-agreement/terms/privacy_policy_full/20260316193944132/20260316193944132.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex w-full items-center gap-2 px-2 py-2 text-sm text-dark-text rounded-lg transition-colors hover:text-theme-color active:text-theme-color"
                  >
                    <Shield className="h-4 w-4 opacity-30" />
                    隐私协议
                  </a>
                </div>
              ) : null}
            </div>
          </div>
        </aside>

        <main className="min-h-0 min-w-0 flex-1 overflow-hidden">
          <PointsSummaryProvider value={pointsSummaryContextValue}>
            {children}
          </PointsSummaryProvider>
        </main>
      </div>
      <PointsCenterModal
        open={pointsCenterOpen}
        summary={pointsSummary}
        loading={pointsLoading}
        error={pointsError}
        userLabel={userLabel}
        onClose={() => setPointsCenterOpen(false)}
        onRetry={() => {
          void loadPointsSummary();
        }}
        onOpenHistory={handleOpenPointsHistory}
        onOpenRechargeRecords={handleOpenPaymentOrders}
        canViewGlobalBilling={canViewGlobalBilling}
        onOpenGlobalBilling={handleOpenGlobalBillingUsage}
        onOpenAdminRecharge={handleOpenAdminRecharge}
        onOpenBuy={handleOpenPointsBuy}
      />
      <PointsBuyModal
        open={pointsBuyOpen}
        onClose={() => {
          setPointsBuyOpen(false);
          setPointsCenterOpen(true);
        }}
        theme={theme}
        paymentProducts={paymentProducts}
        paymentMethods={paymentMethods}
        selectedPaymentMethodId={selectedPaymentMethodId}
        onSelectPaymentMethod={setSelectedPaymentMethodId}
        paymentProductsLoading={paymentProductsLoading}
        purchasingProductId={purchasingProductId}
        paymentPurchaseError={paymentPurchaseError}
        onPurchaseProduct={handlePurchaseProduct}
      />
      {paymentPurchaseOrder ? (
        <PointsPurchaseModal
          open={paymentPurchaseOpen}
          order={paymentPurchaseOrder}
          status={paymentPurchaseStatus}
          refreshing={paymentPurchaseRefreshing}
          error={paymentPurchaseError}
          channelAction={paymentPurchaseChannelAction}
          onRefresh={() => {
            void refreshPaymentPurchase();
          }}
          onClose={() => setPaymentPurchaseOpen(false)}
        />
      ) : null}
      <PointsHistoryModal
        open={pointsHistoryOpen}
        history={pointsHistory}
        loading={pointsHistoryLoading}
        error={pointsHistoryError}
        userLabel={userLabel}
        activeCategory={pointsHistoryCategory}
        dateRange={pointsHistoryDateRange}
        projectFilterKey={pointsHistoryProjectKey}
        sessionFilterKey={pointsHistoryConversationKey}
        sessionOptions={pointsHistorySessionOptions}
        sessionOptionsLoading={pointsHistorySessionOptionsLoading}
        page={pointsHistoryPages[pointsHistoryCategory] ?? 1}
        pageSize={POINTS_HISTORY_PAGE_SIZE}
        onClose={() => setPointsHistoryOpen(false)}
        onRetry={() => {
          void loadPointsHistory({
            category: pointsHistoryCategory,
            page: pointsHistoryPages[pointsHistoryCategory] ?? 1,
            dateRange: pointsHistoryDateRange,
          });
        }}
        onCategoryChange={handlePointsHistoryCategoryChange}
        onDateRangeChange={handlePointsHistoryDateRangeChange}
        onProjectFilterChange={handlePointsHistoryProjectFilterChange}
        onSessionFilterChange={handlePointsHistorySessionFilterChange}
        onPageChange={handlePointsHistoryPageChange}
        onBack={handleBackToPointsCenter}
      />
      <PaymentOrdersModal
        open={paymentOrdersOpen}
        orders={paymentOrders}
        loading={paymentOrdersLoading}
        error={paymentOrdersError}
        page={paymentOrdersPage}
        pageSize={PAYMENT_ORDERS_PAGE_SIZE}
        onClose={() => setPaymentOrdersOpen(false)}
        onRetry={() => {
          void loadPaymentOrders(paymentOrdersPage);
        }}
        onPageChange={handlePaymentOrdersPageChange}
        onBack={handleBackToPointsCenter}
      />
      <GlobalBillingUsageModal
        open={globalBillingOpen}
        usage={globalBillingUsage}
        loading={globalBillingLoading}
        error={globalBillingError}
        dateRange={globalBillingDateRange}
        onClose={() => setGlobalBillingOpen(false)}
        onRetry={() => loadGlobalBillingUsage(globalBillingDateRange)}
        onDateRangeChange={handleGlobalBillingDateRangeChange}
      />
      {canViewGlobalBilling ? (
        <PointsGiftRechargeModal
          open={adminRechargeOpen}
          loading={adminRechargeLoading}
          error={adminRechargeError}
          successMessage={adminRechargeSuccess}
          onClose={() => setAdminRechargeOpen(false)}
          onSubmit={handleSubmitAdminRecharge}
        />
      ) : null}
    </div>
  );
};

export default Layout;
